import { alertMessage, alertType } from '../../components/Alertsv2/alert'
import { useAlertsState } from '../../components/Alertsv2/useAlertState'
import { getDaysUntilExpiration } from '../../utils/alerts'

interface UseAlertFlagProps {
  flag: string | string[]
  tooltip?: string
}

interface UseAlertFlagReturn {
  isFlagIncluded: boolean
  tooltipText: string | undefined
}

export const replaceAlertMessageValues = (
  message: string,
  valuesOriginal?: Record<string, string | number>
): string => {
  const values = { ...valuesOriginal }

  if (values) {
    const valuesKeys = Object.keys(values)

    // the user browser might have a different timezone than the backend, so we need to deal with that
    // we use the provided expiration_date to calculate the days until expiration/due and replace the server values with the calculated ones
    if (valuesKeys.includes('expiration_date') && values.expiration_date) {
      if (valuesKeys.includes('days_until_expiration')) {
        values.days_until_expiration = getDaysUntilExpiration(values.expiration_date.toString())
      } else if (valuesKeys.includes('days_until_due')) {
        values.days_until_due = getDaysUntilExpiration(values.expiration_date.toString())
      }
    }
  }

  const valuesRegex = /\{\{([^|}]+)(?:\|([^}]+))?\}\}/g
  return message
    .replace(valuesRegex, (match, p1, p2) => {
      return values?.[p1] || p2 || ''
    })
    .trim()
}

const SEPARATOR = '; '

export const useAlertFlag = ({ flag, tooltip }: UseAlertFlagProps): UseAlertFlagReturn => {
  const { alerts } = useAlertsState()
  const alertsTypes = alerts
    .map((alert) => [alert.key, alert.message])
    .flat()
    .filter(Boolean)
  const foundAlerts = alertsTypes?.filter((alert) =>
    Array.isArray(flag) ? flag.includes(alert) : flag === alert
  )

  if (!foundAlerts) {
    return {
      isFlagIncluded: false,
      tooltipText: undefined,
    }
  }

  const isFlagIncluded = foundAlerts.length > 0

  let defaultTooltips: string[] = foundAlerts
    .map((foundAlert) => {
      if (foundAlert.startsWith('missing-data')) {
        return alertType['missing-data']
      }

      const alert = alerts.find((alert) => alert.message === foundAlert || alert.key === foundAlert)
      if (!alert) {
        return ''
      }

      const message = alertMessage[alert.message]?.message || ''
      return replaceAlertMessageValues(message, alert.values)
    })
    .filter(Boolean)
  defaultTooltips = Array.from(new Set(defaultTooltips))

  return {
    isFlagIncluded,
    tooltipText: tooltip || defaultTooltips.join(SEPARATOR),
  }
}
